// Approvals — admin queue for payouts in 'needs_approval' state.
//
// Migration 019 (backend) made every payout enter the queue in
// needs_approval. Admins click Approve to dispatch to the provider,
// or Reject with a required reason to terminate the row as failure.
// This page is the operator's surface for that workflow.
//
// Scope:
//   - Admin / superadmin only — gated at the router (RequireAuth role="admin").
//   - Cross-tenant by default; the merchant switcher's currentMerchantId
//     scopes the view when set, so an admin reviewing one merchant doesn't
//     have to filter client-side.
//
// Live view: 15s polling so newly-uploaded CSVs surface fast. Polling
// stops when the queue is empty to avoid burning DB cycles.
import { useMemo, useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { I } from "@/components/Icons";
import { Modal, StatusPill } from "@/components/UI";
import { formatDateTime, fmtCcyMinor } from "@/utils/format";
import { apiErrorMessage } from "@/utils/apiError";
import {
  listNeedsApproval,
  approvePayout,
  rejectPayout,
  type Payout,
} from "@/api/payouts";
import { approveBatch, rejectBatch } from "@/api/batches";
import { groupByBatch, type PayoutGroup } from "@/utils/payoutGroups";
import { useAuth, isAdminRole } from "@/store/auth";
import { useUI } from "@/store/ui";

export function Approvals() {
  const qc = useQueryClient();
  const isAdmin = useAuth((s) => isAdminRole(s.user?.role));
  const currentMerchantId = useUI((s) => s.currentMerchantId);
  const addToast = useUI((s) => s.addToast);

  // Cross-tenant by default; scope to one merchant only when the
  // switcher is set AND the user explicitly picked it. The endpoint
  // takes a number — undefined returns everything.
  const { data: rows = [], isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ["needs-approval", currentMerchantId ?? "all"],
    queryFn: () => listNeedsApproval(currentMerchantId ?? undefined),
    enabled: isAdmin,
    // 15s polling; cheaper than 30s for an active-work surface.
    refetchInterval: (q) => {
      // Stop polling when the queue is empty — no point hammering the
      // backend list endpoint when there's nothing to approve.
      const data = q.state.data as Payout[] | undefined;
      return data && data.length > 0 ? 15_000 : false;
    },
  });

  // Per-row in-flight state. Set<payoutId> rather than a single id so a
  // bulk-click sequence doesn't disable rows that aren't being touched.
  const [acting, setActing] = useState<ReadonlySet<number>>(new Set());

  // Per-batch in-flight state — independent from per-row `acting` so a
  // batch in flight greys out the bulk buttons + all child rows, but
  // single-payout rows in another (orphan) group stay clickable.
  const [batchActing, setBatchActing] = useState<ReadonlySet<number>>(new Set());

  // Reject-reason modal state — null when closed, otherwise the row
  // we're rejecting. Modal mounts in JSX below conditional on this.
  const [rejectTarget, setRejectTarget] = useState<Payout | null>(null);

  // Batch-reject reason modal — separate from rejectTarget because the
  // toast copy + onSubmit handler are different (rejectBatch API call
  // returns counts, not a single Payout).
  const [rejectBatchTarget, setRejectBatchTarget] = useState<
    | {
        batchId: number;
        rowCount: number;
        merchantId: number;
        totalMinor: bigint;
        currency: string;
      }
    | null
  >(null);

  // Group rows by batch_id. Rows with batch_id=null are "orphans"
  // (single-payout endpoint, M2M API) and collect under a synthetic
  // groupKey="orphans" at the bottom. useMemo keyed on the row IDs +
  // length so the group re-derives only when the queue actually
  // changes — not on every keystroke in the merchant switcher.
  const groups = useMemo(() => groupByBatch(rows), [rows]);

  // Expansion state keyed by group.key ("batch-<id>" or "orphans").
  // Default is empty → every group renders collapsed (header only).
  // That's deliberate: when there are 5+ pending batches the un-
  // collapsed table would dump hundreds of rows into one viewport.
  // Operator clicks a header to drill into the batch they're acting on.
  // The Approve all / Reject all buttons stay live regardless of the
  // collapsed state — that's the entire point of the queue.
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());

  function toggleExpanded(key: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function markActing(id: number, on: boolean) {
    setActing((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  async function handleApprove(p: Payout) {
    markActing(p.id, true);
    try {
      const updated = await approvePayout(p.id);
      // Invalidate the queue list AND any per-merchant payouts views so
      // the dispatched row shows up where ops would naturally look.
      void qc.invalidateQueries({ queryKey: ["needs-approval"] });
      void qc.invalidateQueries({ queryKey: ["payouts", p.merchant_id] });
      void qc.invalidateQueries({
        queryKey: ["merchant-payouts", p.merchant_id],
      });
      addToast({
        title: `Approved pay_${p.id}`,
        body: `Now ${updated.status}${
          updated.provider_txn_id ? ` · txn ${updated.provider_txn_id}` : ""
        }.`,
        variant: "success",
      });
    } catch (err) {
      addToast({
        title: `Could not approve pay_${p.id}`,
        body: apiErrorMessage(
          err,
          "The transition failed. Refresh the queue — another admin may have just acted.",
        ),
        variant: "error",
      });
      // Refetch so the row reflects whatever the winning admin did.
      void refetch();
    } finally {
      markActing(p.id, false);
    }
  }

  function markBatchActing(batchId: number, on: boolean) {
    setBatchActing((prev) => {
      const next = new Set(prev);
      if (on) next.add(batchId);
      else next.delete(batchId);
      return next;
    });
  }

  // handleApproveBatch fires one bulk-approve POST that dispatches every
  // needs_approval row in the batch. Backend warns: 500-row batches can
  // take ~3 minutes (sequential dispatch). The UI disables the button +
  // shows a spinner; the operator must not navigate away.
  async function handleApproveBatch(
    batchId: number,
    rowCount: number,
    merchantId: number,
  ) {
    markBatchActing(batchId, true);
    try {
      const result = await approveBatch(batchId);
      void qc.invalidateQueries({ queryKey: ["needs-approval"] });
      void qc.invalidateQueries({ queryKey: ["payouts", merchantId] });
      void qc.invalidateQueries({ queryKey: ["merchant-payouts", merchantId] });
      void qc.invalidateQueries({ queryKey: ["batch", batchId] });
      const skipped = result.already_handled_ids.length;
      const errs = result.dispatch_errors;
      addToast({
        title: `Approved ${result.approved_count}/${rowCount} from batch_${batchId}`,
        body: [
          skipped > 0
            ? `${skipped} row${skipped === 1 ? "" : "s"} were already handled (concurrent admin).`
            : "",
          errs > 0
            ? `${errs} dispatch error${errs === 1 ? "" : "s"} — reconciler will catch up.`
            : "",
        ]
          .filter(Boolean)
          .join(" "),
        variant: errs > 0 ? "error" : "success",
      });
    } catch (err) {
      addToast({
        title: `Could not approve batch_${batchId}`,
        body: apiErrorMessage(err, "The bulk approve failed. Refresh and try again."),
        variant: "error",
      });
      void refetch();
    } finally {
      markBatchActing(batchId, false);
    }
  }

  if (!isAdmin) {
    return (
      <div className="page">
        <h1>Approvals</h1>
        <p className="dim">
          This queue is admin-only. Your account doesn't have the role
          required to act on it.
        </p>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="eyebrow">Console</div>
          <h1>Approvals</h1>
          <div className="sub">
            {isLoading
              ? "Loading…"
              : rows.length === 0
                ? "No payouts awaiting approval"
                : `${rows.length} awaiting approval`}
          </div>
        </div>
        <div className="actions" style={{ display: "flex", alignItems: "center", gap: 16 }}>
          {/* Page-level total — anchor at top-right so the operator
              sees the total liability before clicking Approve all. The
              eyebrow on top mirrors the page-head visual rhythm. */}
          {!isLoading && rows.length > 0 && (
            <div style={{ textAlign: "right" }}>
              <div
                className="eyebrow"
                style={{ fontSize: 10, letterSpacing: "0.06em" }}
              >
                Pending total
              </div>
              <div
                style={{
                  fontSize: 22,
                  fontWeight: 600,
                  fontVariantNumeric: "tabular-nums",
                  lineHeight: 1.15,
                }}
              >
                {fmtCcyMinor(
                  rows.reduce((acc, p) => acc + p.amount_minor, 0n),
                  rows[0].currency,
                )}
              </div>
            </div>
          )}
          <button
            className="btn ghost sm"
            type="button"
            onClick={() => void refetch()}
            disabled={isLoading}
            title="Refresh queue"
          >
            <I.Refresh
              size={12}
              style={{
                animation: isFetching
                  ? "spin 800ms linear infinite"
                  : undefined,
              }}
            />{" "}
            Refresh
          </button>
        </div>
      </div>

      <div className="card flat">
        {isLoading ? (
          <div className="empty">
            <div className="dim" style={{ fontSize: 13 }}>
              Loading…
            </div>
          </div>
        ) : isError ? (
          <div className="empty">
            <p>Couldn't load the approval queue.</p>
          </div>
        ) : rows.length === 0 ? (
          <div className="empty">
            <div className="icon">
              <I.Users size={16} />
            </div>
            <h3>Queue is empty</h3>
            <p>
              No payouts currently need approval. Newly-uploaded CSV batches
              and individual payout requests will appear here.
            </p>
          </div>
        ) : (
          // Group-by-batch render. A single CSV upload reserves up to 500
          // needs_approval rows that all share the same batch_id; the group
          // header carries the "Approve all (N)" bulk button so the
          // operator doesn't click Approve 500 times. Orphan rows
          // (single-payout endpoint, M2M API — batch_id=null) collect into
          // a final group with per-row buttons only.
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {groups.map((g) => {
              const batchInFlight =
                g.batchId != null && batchActing.has(g.batchId);
              const isExpanded = expanded.has(g.key);
              return (
                <div key={g.key} className="card flat" style={{ padding: 0 }}>
                  <BatchGroupHeader
                    group={g}
                    batchInFlight={batchInFlight}
                    isExpanded={isExpanded}
                    onToggle={() => toggleExpanded(g.key)}
                    onApproveBatch={() => {
                      if (g.batchId == null) return;
                      void handleApproveBatch(
                        g.batchId,
                        g.rows.length,
                        g.rows[0].merchant_id,
                      );
                    }}
                    onRejectBatch={() => {
                      if (g.batchId == null) return;
                      setRejectBatchTarget({
                        batchId: g.batchId,
                        rowCount: g.rows.length,
                        merchantId: g.rows[0].merchant_id,
                        totalMinor: g.totalMinor,
                        currency: g.rows[0].currency,
                      });
                    }}
                  />
                  {isExpanded && (
                  <table className="tbl">
                    <thead>
                      <tr>
                        <th>Payout</th>
                        <th>Merchant</th>
                        <th className="num">Amount</th>
                        <th>Mode</th>
                        <th>Request ID</th>
                        <th>Created</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {g.rows.map((p) => {
                        const inFlight = acting.has(p.id) || batchInFlight;
                        return (
                          <tr key={p.id}>
                            <td>
                              <div className="cell-name mono">pay_{p.id}</div>
                              <div className="cell-id">
                                <StatusPill status={p.status} />
                              </div>
                            </td>
                            <td className="mono">mer_{p.merchant_id}</td>
                            <td className="num mono">
                              {fmtCcyMinor(p.amount_minor, p.currency)}
                            </td>
                            <td className="dim">
                              {p.txn_mode === 1
                                ? "IMPS"
                                : p.txn_mode === 2
                                  ? "NEFT"
                                  : p.txn_mode}
                            </td>
                            <td className="mono dim" style={{ fontSize: 11 }}>
                              {p.request_id}
                            </td>
                            <td className="dim">
                              {formatDateTime(p.created_at)}
                            </td>
                            <td className="right">
                              <div
                                style={{
                                  display: "flex",
                                  gap: 6,
                                  justifyContent: "flex-end",
                                }}
                              >
                                <button
                                  className="btn ghost sm"
                                  type="button"
                                  onClick={() => setRejectTarget(p)}
                                  disabled={inFlight}
                                >
                                  <I.X size={11} /> Reject
                                </button>
                                <button
                                  className="btn accent sm"
                                  type="button"
                                  onClick={() => handleApprove(p)}
                                  disabled={inFlight}
                                >
                                  {acting.has(p.id) ? (
                                    "Approving…"
                                  ) : (
                                    <>
                                      <I.Check size={11} /> Approve
                                    </>
                                  )}
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {rejectTarget && (
        <RejectModal
          payout={rejectTarget}
          open={true}
          onClose={() => setRejectTarget(null)}
          onRejected={() => {
            void qc.invalidateQueries({ queryKey: ["needs-approval"] });
            void qc.invalidateQueries({
              queryKey: ["payouts", rejectTarget.merchant_id],
            });
            void qc.invalidateQueries({
              queryKey: ["merchant-payouts", rejectTarget.merchant_id],
            });
            setRejectTarget(null);
          }}
        />
      )}

      {rejectBatchTarget && (
        <BatchRejectModal
          target={rejectBatchTarget}
          open={true}
          onClose={() => setRejectBatchTarget(null)}
          onRejected={() => {
            void qc.invalidateQueries({ queryKey: ["needs-approval"] });
            void qc.invalidateQueries({
              queryKey: ["payouts", rejectBatchTarget.merchantId],
            });
            void qc.invalidateQueries({
              queryKey: ["merchant-payouts", rejectBatchTarget.merchantId],
            });
            void qc.invalidateQueries({
              queryKey: ["batch", rejectBatchTarget.batchId],
            });
            setRejectBatchTarget(null);
          }}
        />
      )}
    </div>
  );
}

// ─── Batch group header ───────────────────────────────────────────────────────

function BatchGroupHeader({
  group,
  batchInFlight,
  isExpanded,
  onToggle,
  onApproveBatch,
  onRejectBatch,
}: {
  group: PayoutGroup;
  batchInFlight: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  onApproveBatch: () => void;
  onRejectBatch: () => void;
}) {
  const isOrphan = group.batchId == null;
  const currency = group.rows[0]?.currency ?? "INR";
  const merchantId = group.rows[0]?.merchant_id;
  // borderBottom only when the rows are visible — a collapsed header
  // with a border bottom reads as "table starts below" and the empty
  // space afterwards looks like a render bug.
  const headerBorder = isExpanded
    ? "1px solid var(--line, rgba(0,0,0,0.06))"
    : "none";
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "14px 16px",
        borderBottom: headerBorder,
        gap: 16,
      }}
    >
      {/* Chevron + identity. Clicking ANY of this region toggles
          expansion; the bulk buttons on the right are isolated below
          so their clicks don't bubble to the toggle. <button> on the
          chevron itself gives a real focus ring + keyboard activation
          via Enter/Space without us hand-rolling aria. */}
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isExpanded}
        aria-label={
          isExpanded
            ? `Collapse ${isOrphan ? "single-payout rows" : `batch_${group.batchId}`}`
            : `Expand ${isOrphan ? "single-payout rows" : `batch_${group.batchId}`}`
        }
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          flex: 1,
          minWidth: 0,
          padding: 0,
          border: "none",
          background: "transparent",
          cursor: "pointer",
          textAlign: "left",
          color: "inherit",
          font: "inherit",
        }}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 16,
            height: 16,
            color: "var(--ink-2, currentColor)",
            transition: "transform 120ms ease",
          }}
        >
          {isExpanded ? <I.ChevronDown size={14} /> : <I.ChevronRight size={14} />}
        </span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>
            {isOrphan ? (
              <>Single-payout rows</>
            ) : (
              <>
                Batch{" "}
                {/* The link's onClick stops propagation so a click on
                    batch_<id> opens the detail page instead of
                    toggling collapse. */}
                <Link
                  to={`/payouts/batches/${group.batchId}`}
                  className="mono"
                  title="Open batch detail"
                  onClick={(e) => e.stopPropagation()}
                >
                  batch_{group.batchId}
                </Link>
                {merchantId != null && (
                  <>
                    {" "}
                    · <span className="mono dim">mer_{merchantId}</span>
                  </>
                )}
              </>
            )}
          </div>
          <div className="dim" style={{ fontSize: 12, marginTop: 2 }}>
            {group.rows.length} row{group.rows.length === 1 ? "" : "s"}{" "}
            awaiting approval
            {isOrphan && (
              <>
                {" "}
                · these came in one-by-one (single-payout endpoint or API)
              </>
            )}
          </div>
        </div>
      </button>

      {/* Per-batch total — visual anchor at the right of the header so
          the operator reads the disbursement amount BEFORE the Approve
          all button. Tabular-nums keeps digit columns aligned across
          stacked groups; the size matches the page-level total so the
          eye can compare them at a glance. */}
      <div style={{ textAlign: "right" }}>
        <div
          className="eyebrow"
          style={{ fontSize: 10, letterSpacing: "0.06em" }}
        >
          Batch total
        </div>
        <div
          style={{
            fontSize: 20,
            fontWeight: 600,
            fontVariantNumeric: "tabular-nums",
            lineHeight: 1.15,
          }}
        >
          {fmtCcyMinor(group.totalMinor, currency)}
        </div>
      </div>

      {!isOrphan && (
        <div style={{ display: "flex", gap: 6 }}>
          <button
            className="btn ghost sm"
            type="button"
            onClick={onRejectBatch}
            disabled={batchInFlight}
          >
            <I.X size={11} /> Reject all ({group.rows.length})
          </button>
          <button
            className="btn accent sm"
            type="button"
            onClick={onApproveBatch}
            disabled={batchInFlight}
            title="Approve every row in this batch in one call"
          >
            {batchInFlight ? (
              "Approving…"
            ) : (
              <>
                <I.Check size={11} /> Approve all ({group.rows.length})
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Reject modal ─────────────────────────────────────────────────────────────

function RejectModal({
  payout,
  open,
  onClose,
  onRejected,
}: {
  payout: Payout;
  open: boolean;
  onClose: () => void;
  onRejected: () => void;
}) {
  const addToast = useUI((s) => s.addToast);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Backend caps reason at 500 chars (binding:max=500). Mirror in UI so
  // the operator gets a clean inline counter instead of a 400 after
  // submit.
  const MAX = 500;
  const trimmed = reason.trim();
  const canSubmit = !busy && trimmed.length > 0 && trimmed.length <= MAX;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      await rejectPayout(payout.id, trimmed);
      addToast({
        title: `Rejected pay_${payout.id}`,
        body: `Reason recorded. The row is marked as failure with error_code='admin_rejected'.`,
        variant: "success",
      });
      onRejected();
    } catch (err) {
      setError(
        apiErrorMessage(
          err,
          "Could not reject — the row may have already been acted on by another admin.",
        ),
      );
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      busy={busy}
      title={`Reject pay_${payout.id}?`}
      foot={
        <>
          <button
            className="btn ghost"
            type="button"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <div className="flex-1" />
          <button
            className="btn danger"
            type="submit"
            form="reject-form"
            disabled={!canSubmit}
          >
            {busy ? "Rejecting…" : "Reject payout"}
          </button>
        </>
      }
    >
      <form id="reject-form" onSubmit={handleSubmit} className="stack-16">
        <div className="dim" style={{ fontSize: 13, lineHeight: 1.5 }}>
          The payout will be marked as <strong>failure</strong> with{" "}
          <span className="mono">error_code=admin_rejected</span>. The
          reason you provide is persisted on the row and lands in the
          audit log under <span className="mono">payout.admin_reject</span>.
          No money moves.
        </div>
        <div className="field">
          <label className="label" htmlFor="reject-reason">
            Reason{" "}
            <span style={{ color: "var(--ink-3)" }}>
              · {trimmed.length}/{MAX}
            </span>
          </label>
          <textarea
            id="reject-reason"
            className="input"
            rows={4}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            autoFocus
            placeholder="e.g. Beneficiary doesn't match invoice. Operator to re-upload."
          />
        </div>
        {error && (
          <div style={{ color: "var(--warn, #b42318)", fontSize: 12 }}>
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
}

// ─── Batch reject modal ───────────────────────────────────────────────────────

// BatchRejectModal mirrors RejectModal but calls rejectBatch (bulk).
// One reason applies to every needs_approval row in the batch; the
// backend stamps `rejection_reason` on each and writes audit rows
// under `payout.admin_reject`. No provider call — no money moves.
function BatchRejectModal({
  target,
  open,
  onClose,
  onRejected,
}: {
  target: {
    batchId: number;
    rowCount: number;
    merchantId: number;
    totalMinor: bigint;
    currency: string;
  };
  open: boolean;
  onClose: () => void;
  onRejected: () => void;
}) {
  const addToast = useUI((s) => s.addToast);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Same 500-char ceiling as single-row rejection — backend enforces
  // binding:max=500 on the bulk endpoint as well.
  const MAX = 500;
  const trimmed = reason.trim();
  const canSubmit = !busy && trimmed.length > 0 && trimmed.length <= MAX;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const result = await rejectBatch(target.batchId, trimmed);
      const skipped = result.already_handled_ids.length;
      addToast({
        title: `Rejected ${result.rejected_count}/${target.rowCount} from batch_${target.batchId}`,
        body:
          skipped > 0
            ? `${skipped} row${skipped === 1 ? "" : "s"} were already handled (concurrent admin).`
            : "Every needs_approval row in the batch was rejected. No money moved.",
        variant: "success",
      });
      onRejected();
    } catch (err) {
      setError(
        apiErrorMessage(
          err,
          "Could not reject — refresh the queue and try again.",
        ),
      );
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      busy={busy}
      title={`Reject all ${target.rowCount} rows in batch_${target.batchId}?`}
      foot={
        <>
          <button
            className="btn ghost"
            type="button"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <div className="flex-1" />
          <button
            className="btn danger"
            type="submit"
            form="reject-batch-form"
            disabled={!canSubmit}
          >
            {busy
              ? "Rejecting…"
              : `Reject ${target.rowCount} row${target.rowCount === 1 ? "" : "s"}`}
          </button>
        </>
      }
    >
      <form
        id="reject-batch-form"
        onSubmit={handleSubmit}
        className="stack-16"
      >
        <div className="dim" style={{ fontSize: 13, lineHeight: 1.5 }}>
          Every <strong>{target.rowCount}</strong> needs_approval row in this
          batch (totalling{" "}
          <strong>{fmtCcyMinor(target.totalMinor, target.currency)}</strong>)
          will be marked <strong>failure</strong> with{" "}
          <span className="mono">error_code=admin_rejected</span> and your
          reason persisted on each row. No provider call. No money moves.
        </div>
        <div className="field">
          <label className="label" htmlFor="reject-batch-reason">
            Reason{" "}
            <span style={{ color: "var(--ink-3)" }}>
              · {trimmed.length}/{MAX}
            </span>
          </label>
          <textarea
            id="reject-batch-reason"
            className="input"
            rows={4}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            autoFocus
            placeholder="e.g. Wrong source rail — operator to re-upload with the right bank account."
          />
        </div>
        {error && (
          <div style={{ color: "var(--warn, #b42318)", fontSize: 12 }}>
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
}
